import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeLabelsEnum, queryKeys } from '@/app/utils/constants';
import { useTranslations } from 'use-intl';
import { DocumentsContextProvider } from '@/app/components/shared/EntityDocuments/DocumentsContext';
import { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { FolderIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { deleteEPADocuments } from '@/app/services/earlyPhaseAssessmentsService';
import { ResourceConfig } from '@/app/components/features/evaluations/ResourceView/config';
import { SummarizationNamespaces, DocumentTypes } from '@/app/utils/constants';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderButton,
} from '@/app/ui/components/header';
import { MainScrollArea } from '@/app/ui/components/main';
import { getEPADetailsQuery } from '@/app/hooks/queries/useEarlyPhaseAssessment';
import { FilesList } from '@/app/components/shared/EntityDocuments/FilesList';
import { FileUploadDialog } from '@/app/components/shared/EntityDocuments/FileUploadDialog/FileUploadDialog';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/early-phase-assessments/$earlyPhaseAssessmentId/_layout/(home)/files',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.epaHome');
  const tFiles = useTranslations('pages.files');
  const { earlyPhaseAssessmentId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const config = ResourceConfig[SummarizationNamespaces.EARLY_PHASE_ASSESSMENT];
  const [open, setOpen] = useState(false);
  const { data: evaluation } = useSuspenseQuery(getEPADetailsQuery(earlyPhaseAssessmentId));

  return (
    <DocumentsContextProvider
      resourceId={earlyPhaseAssessmentId}
      documentsType={config.documentType}
    >
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={FolderIcon} />
                {t('nav.files')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderButton aria-label={tFiles('addFiles')} onClick={() => setOpen(true)}>
          <HugeiconsIcon icon={PlusSignIcon} />
        </HeaderButton>
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={earlyPhaseAssessmentId}
          summaryType={SummarizationNamespaces.EARLY_PHASE_ASSESSMENT}
        />
        <FilesList
          resourceId={earlyPhaseAssessmentId}
          resourceType={DocumentTypes.EARLY_PHASE_ASSESSMENT}
          onAddClickAction={() => setOpen(true)}
          deleteDocumentsAction={deleteEPADocuments}
          detailsQueryKey={[queryKeys.EARLY_PHASE_ASSESSMENTS_DETAILS, earlyPhaseAssessmentId]}
        />
      </MainScrollArea>
      <FileUploadDialog
        resourceId={earlyPhaseAssessmentId}
        resourceType={DocumentTypes.EARLY_PHASE_ASSESSMENT}
        open={open}
        onOpenChange={setOpen}
        detailsQueryKey={[queryKeys.EARLY_PHASE_ASSESSMENTS_DETAILS, earlyPhaseAssessmentId]}
      />
    </DocumentsContextProvider>
  );
}
